import React from 'react';
import { useIncomingInvoicesQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { PendingInvoice } from './pendingInvoicesMeta';

/**
 * Consumer contract for the `PendingInvoices` widget.
 *
 * Wraps `useIncomingInvoicesQuery` (the `/purchases/incoming-invoices`
 * endpoint) in the widget's `{ data, error, isLoading, onRetry }` shape — the
 * same mapping as `useRevenueSummary` — plus an `onImport(invoiceId)` adapter
 * for the per-row CTA. The queryFn never rejects, so `error` is read from the
 * failed `ApiResponse` envelope, not `query.error`. The list is sorted
 * client-side, so the hook flattens the single fetched page into `data`.
 *
 * `onImport` still logs to the console: importing an invoice is a mutation,
 * and the backend has no such endpoint yet — wiring it is a follow-up.
 */
export const usePendingInvoices = () => {
    const query = useIncomingInvoicesQuery();

    // Keep the last successful page so a failed refetch — which replaces
    // query.data with a success:false envelope rather than rejecting — does
    // not wipe rows already on screen (the container only suppresses the
    // boundary while items are visible).
    const lastItemsRef = React.useRef<PendingInvoice[]>([]);
    if (query.data?.success) {
        lastItemsRef.current = query.data.response.items;
    }

    const data = React.useMemo<PendingInvoice[]>(
        () =>
            query.data?.success
                ? query.data.response.items
                : lastItemsRef.current,
        [query.data],
    );

    // While a retry is in flight TanStack keeps the previous failed envelope
    // in `query.data`; gate on `isFetching` so the ErrorBoundary's Retry
    // doesn't immediately re-throw the stale failure (which would otherwise
    // need a second click to recover).
    const error = React.useMemo<ErrorResponse | null>(
        () =>
            !query.isFetching && query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.isFetching, query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    const onImport = React.useCallback((invoiceId: string) => {
        console.info(`[purchases] import invoice requested: ${invoiceId}`);
    }, []);

    return {
        data,
        error,
        // A retry before any success shows the skeleton (not the empty/throw
        // states); a background refetch over existing data does not.
        isLoading:
            query.isPending || (query.isFetching && !query.data?.success),
        onImport,
        onRetry,
    } as const;
};
